<script lang="ts" setup>
import { Expand } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from "vue";
import {
  FormConcurrencyMode,
  type FormDefinition,
  type FormElementBase,
  FormElementRule,
  FormElementType,
  FormRuleType,
  FormTextFormElement,
  FormType,
  LabelledOption,
  SectionAssignables,
  WorkflowDefinition,
  WorkflowEventTriggerType,
} from "~/api";
import { drawerWidth, emptyGuid } from "~/utils/constants";

import { apiClient } from "~/utils/apiClient";
import { getMessageTime, splitByCaps } from "~/utils/miscUtils";

import { HubConnection } from "@microsoft/signalr";
import { useRefHistory, useStorage } from "@vueuse/core";
import { onBeforeRouteLeave } from "vue-router";
import { type FormElementDefault } from "~/NewFormBuilder/components/FormComponents/defaultComponents";
import { getNewQuestionCode } from "~/utils/formUtils";
import { showError } from "~/utils/handleAPIError";
import { hubManager } from "~/utils/hubManager";
import { isFormElementBasedCondition } from "~/utils/typeGuards";
import {
  commitDraft,
  confirmCommitDraft,
  confirmDiscardDraft,
  discardDraft,
  discardDraftIfPristine,
  getDraftStatus,
  resolveEditableForm,
} from "../../composables/useFormDraft";
import { FormPage } from "../../composables/useFormResponse";
import FormComponentRenderer from "../FormComponents/FormComponentRenderer.vue";
import FormRendererInner from "../Renderer/FormRendererInner.vue";
import DroppablePad from "./DroppablePad.vue";
import FormDesignerPalette from "./FormDesignerPalette.vue";
import ElementEditor from "./FormEditors/ElementEditor.vue";
import FormHistoryTab from "./FormEditors/FormHistoryTab.vue";
import FormPropertiesEditor from "./FormEditors/FormPropertiesEditor.vue";
import FormRule from "./FormEditors/FormRule.vue";
import FormRulesTable from "./FormEditors/FormRulesTable.vue";
import ListsTab from "./FormEditors/ListsTab.vue";
import VersionsTab from "./FormEditors/VersionsTab.vue";
import TableOfContents from "./TableOfContents.vue";

import {
  dragDropInjectKey,
  useDragDrop,
} from "~/NewFormBuilder/composables/useDragDrop";
// import { Setting } from "@element-plus/icons-vue";

import FormJsonUploader from "./FormEditors/FormJsonUploader.vue";

import CompliancePalette from "~/Compliance/components/CompliancePalette.vue";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { useMyInfoStore } from "~/stores/MyInfoStore";

/** Local copy of form from API, so we can edit the ref */
const form = defineModel<FormDefinition>({ required: true });

/*
 * Draft lifecycle (issue 1014): forms that are in use (responses exist, or workflow
 * instances can still create responses) are never edited directly. Such a form opens
 * READ-ONLY with an "Edit as Draft" button; clicking it creates a server-side draft
 * snapshot and unlocks editing on that draft. Commit releases the draft changes to the
 * live form at once, Discard throws them away — both return to the read-only state. The
 * server also rejects direct saves to in-use forms, so this is UX on a hard guarantee.
 */
/** True when this form is in use, so editing requires a draft. */
const requiresDraft = ref<boolean>(false);
/** True when edits go to the form's draft instead of the live definition. */
const isDraftMode = ref<boolean>(false);
/** Last-saved timestamp of the draft, passed to commit as a concurrency check. */
const draftUpdatedAt = ref<string | undefined>();
/** True once the draft was saved in this session — blocks the pristine auto-cleanup. */
const draftSavedThisSession = ref<boolean>(false);
/** True when the draft already had saved changes vs the live form when resumed. */
const draftHasSavedChanges = ref<boolean>(false);
/** Suppresses reacting to our own DraftCommitted/DraftDiscarded hub events. */
const suppressDraftEvents = ref<boolean>(false);
/** True while creating/resuming a draft ("Edit as Draft" spinner). */
const enteringDraft = ref<boolean>(false);
/** True while committing the draft (Commit button spinner). */
const committingDraft = ref<boolean>(false);
/** True while discarding the draft (Discard button spinner). */
const discardingDraft = ref<boolean>(false);
/** Guid the draft state was last resolved for, to avoid re-resolving on model swaps. */
let draftResolvedForGuid: string | undefined;

/**
 * Determines whether this form is in use (edits require a draft) and whether a draft is
 * already active. An in-use form with no active draft is left read-only until the user
 * clicks "Edit as Draft"; if a draft already exists it is resumed and editing unlocks.
 */
const resolveDraftState = async () => {
  requiresDraft.value = false;
  isDraftMode.value = false;
  draftUpdatedAt.value = undefined;
  draftSavedThisSession.value = false;
  draftHasSavedChanges.value = false;
  try {
    const status = await getDraftStatus(form.value);
    requiresDraft.value = status.requiresDraft;
    // An in-use form is locked by the canEditForm computed until draft mode is entered.
    if (status.requiresDraft && status.hasActiveDraft) {
      // A draft already exists — resume straight into editing.
      await enterDraftMode(true);
    }
  } catch (error) {
    showError("Failed to check the form's draft status.", error);
  }
};

/** Creates or resumes the draft, swaps the model to it, and unlocks editing. */
const enterDraftMode = async (silentResume = false) => {
  enteringDraft.value = true;
  try {
    const resolution = await resolveEditableForm(form.value, {
      silentResume,
    });
    if (resolution.isDraftMode) {
      form.value = resolution.form;
      isDraftMode.value = true;
      draftUpdatedAt.value = resolution.draftUpdatedAt;
      draftSavedThisSession.value = false;
      draftHasSavedChanges.value = !!resolution.draftHasSavedChanges;
      // canEditForm (computed) unlocks now that isDraftMode is true
      nextTick(() => history.clear());
    }
  } catch (error) {
    showError("Failed to open the form's draft.", error);
  } finally {
    enteringDraft.value = false;
  }
};

watch(
  () => form.value.guid,
  async (guid) => {
    if (!guid || draftResolvedForGuid === guid) {
      return;
    }
    draftResolvedForGuid = guid;
    await resolveDraftState();
  },
  { immediate: true },
);
/** The guid of the component we are currently editing the properties of. Don't edit this directly, use the 'editingComponent' instead. */
const _editingComponentGuid = ref<string | undefined>();
/** The component we are currently editing the properties of */
const editingComponent = computed<FormElementBase | undefined>({
  get: () =>
    form.value.components.find((c) => c.guid === _editingComponentGuid.value),

  set: (val) => (_editingComponentGuid.value = val?.guid),
});
/** Currently displayed page number of the form. */
const currentPageNumber = ref<number>(1);
/** All pages of the form, synced from the FormRendererInner component. Used here for the TOC */
const formPages = ref<FormPage[]>([]);
/** Last time the save changes was called. */
const lastSaveTime = ref<Date>(new Date());
/** True if we are currently dragging around a component. */
const isDragging = ref<boolean>(false);
/** True if we can drop this in a repeater component. */
const canDropInRepeater = ref<boolean>(true);
/** True if we are currently adding/editing form rules. */
const isRuleDrawerOpen = ref<boolean>(false);
/** True if the form properties editor is open */
// const isFormPropertiesEdit = ref<boolean>(false);
/** True if we want to display question codes above each element. Persisted in local storage */
const showQuestionCodes = useStorage("show-question-codes", false);
const autoSaveForm = useStorage("auto-save-form", false);
const splitFormPages = useStorage("split-form-pages", false);
const applyRules = useStorage("apply-rules", false);
const showArchived = useStorage("show-archived", false);

const formUploaderVisible = ref<boolean>(false);
/** Need open counter so that :key is different every time */
const uploaderOpenCounter = ref(0);

/** The tab that is currently active - remembered in sessionStorage */
const activeTab = useStorage("formDesignerActiveTab", "design", sessionStorage);
const activeElementEditorTab = ref<string>("properties");

/** Number of archived components */
const numberArchived = computed<number>(
  () => form.value.components.filter((c) => c.isArchived).length,
);

/** Keeps track of the form's history */
const history = useRefHistory(form, {
  deep: true,
  flush: "sync",
  capacity: 10,
});

/** called when a save is needed */
const emit = defineEmits<{
  (e: "saveNeeded", saveNeeded: boolean): boolean;
  (e: "formSaved", form: FormDefinition): FormDefinition;
  (e: "closeDrawer"): void;
  (e: "deleted", form: FormDefinition): void;
  (e: "duplicated", form: FormDefinition): void;
}>();

const props = defineProps<{
  workflowDefinition?: WorkflowDefinition;
  complianceItem?: boolean;
  drawerClosed?: boolean;
}>();

const handleDeleteFormAndResponses = async (formDefinitionGuid: string) => {
  try {
    await ElMessageBox.confirm(
      `This form has ${form.value.formResponseCount} associated form responses. Are you sure you want to delete this form and all its responses?`,
      "Delete Form",
      {
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        closeOnPressEscape: false,
        closeOnClickModal: false,
        showClose: false,
        type: "warning",
      },
    );
    if (!formDefinitionGuid) {
      return;
    }
    try {
      const res =
        await apiClient.formDefinition.deleteFormDefinition(formDefinitionGuid);
      ElMessage.success("Form and associated responses have been deleted");
      emit("closeDrawer");
      emit("saveNeeded", false);
      emit("deleted", res);
    } catch (error) {
      showError(error);
    }
  } catch {
    // do nothing
  }
};

const isDuplicating = ref(false);

const handleDuplicateForm = async (formDefinitionGuid: string) => {
  if (!formDefinitionGuid) {
    return;
  }
  try {
    isDuplicating.value = true;
    const res =
      await apiClient.formDefinition.duplicateFormDefinition(
        formDefinitionGuid,
      );
    ElMessage.success(`Form duplicated as "${res.name}"`);
    emit("duplicated", res);
  } catch (error) {
    showError(error);
  } finally {
    isDuplicating.value = false;
  }
};
const complianceItem = ref(props.complianceItem);
const isDrawerClosed = computed(() => props.drawerClosed);
const isDrawerOpen = computed(() => !isDrawerClosed.value);

/** List of elements that can't be deleted */
const elementsThatCantBeDeleted = computed<string[]>(() => {
  // if there is only one section, don't allow it to be deleted
  const sections = form.value.components.filter(
    (c) => c.type === FormElementType.SECTION,
  );
  let result: string[] = [];

  if (sections.length === 1) {
    result.push(sections[0].guid);
  }

  const sectionsWithComponents = sections
    .map((s) => s.guid)
    .filter((guid) => form.value.components.some((c) => c.parentGuid === guid));
  result = result.concat(sectionsWithComponents);

  return result;
});

/** Whether any edits have been made and a save will be needed to persist the update to the DB */
const saveNeeded = computed<boolean>(() => history.history.value.length > 1);

/*
 * Scoped element saves (issue #1015). We snapshot each element's serialized state at the
 * last successful save. On the next save we diff against that snapshot to persist only the
 * elements that actually changed — the changed elements go in one batched request
 * (updateDraftElements in draft mode, updateFormElements on a not-in-use form) instead of a
 * whole-form save. Rules/section-rules/form-rules and form-level scalars are not
 * per-element, so a change to those (or a questionCode rename, which rewrites rules) falls
 * back to the whole-form/whole-draft save.
 */
/** element guid → serialized element JSON, as of the last successful save. */
let savedElementSnapshots = new Map<string, string>();
/** Serialized non-element parts (rules/formRules/sectionRules/scalars) at last save. */
let savedNonElementSnapshot = "";

const serializeElement = (c: FormElementBase): string =>
  JSON.stringify({ ...c, formOrder: undefined });

const serializeNonElementParts = (f: FormDefinition): string =>
  JSON.stringify({
    rules: f.rules,
    formRules: f.formRules,
    sectionRules: f.sectionRules,
    name: f.name,
    formCode: f.formCode,
    metaInfo: f.metaInfo,
  });

/** Records the current form as the "saved" baseline for future scoped-save diffs. */
const snapshotSavedState = (f: FormDefinition) => {
  savedElementSnapshots = new Map(
    f.components.map((c) => [c.guid, serializeElement(c)]),
  );
  savedNonElementSnapshot = serializeNonElementParts(f);
};

/**
 * Whether anything other than individual element bodies changed since the last save
 * (rules/scalars, element added/removed, or reordered) — those require a whole-form save.
 */
const nonElementPartsChanged = (f: FormDefinition): boolean => {
  if (serializeNonElementParts(f) !== savedNonElementSnapshot) {
    return true;
  }
  // Added or removed elements, or a changed set of guids, needs the whole-form path.
  if (f.components.length !== savedElementSnapshots.size) {
    return true;
  }
  return f.components.some((c) => !savedElementSnapshots.has(c.guid));
};

/** Elements whose serialized body changed since the last save. */
const changedElements = (f: FormDefinition): FormElementBase[] =>
  f.components.filter(
    (c) => serializeElement(c) !== savedElementSnapshots.get(c.guid),
  );

/** True when the element order changed since the last save. */
const elementOrderChanged = (f: FormDefinition): boolean => {
  const savedOrder = [...savedElementSnapshots.keys()];
  return f.components.some((c, i) => savedOrder[i] !== c.guid);
};

/**
 * Whether the draft has anything worth committing: either unsaved edits in this session,
 * or changes already saved to the draft (this session or a resumed one). A pristine draft
 * with no changes vs the live form has nothing to commit.
 */
const draftHasChanges = computed<boolean>(
  () => saveNeeded.value || draftHasSavedChanges.value,
);

const handleClickComponent = (
  component: FormElementBase,
  event?: MouseEvent,
) => {
  // prevents the click from propagating to the parent div when we click on an element in a repeater
  event?.stopPropagation();
  const eventElements = [
    FormElementType.NUMBER,
    FormElementType.MONEY,
    FormElementType.SIMPLE_TEXT,
    FormElementType.RADIO_BUTTONS,
    FormElementType.CHECKBOXES,
  ];
  //Handle active tab changes when the element does not contain the tab.
  if (
    editingComponent.value &&
    ((editingComponent.value.type === FormElementType.SECTION &&
      component.type !== FormElementType.SECTION) ||
      (eventElements.includes(editingComponent.value.type) &&
        !eventElements.includes(component.type))) &&
    activeElementEditorTab.value === "events"
  ) {
    activeElementEditorTab.value = "properties";
  }
  if (
    editingComponent.value?.type === FormElementType.SECTION &&
    component.type !== FormElementType.SECTION &&
    activeElementEditorTab.value === "Access"
  ) {
    activeElementEditorTab.value = "properties";
  }

  editingComponent.value = component;
};

/**
 * This function takes in the section guid and its index in the form components array.
 * It updates the parent guid of all the items after the dropped section until the element with the same parent guid (aka the next Section).
 * @param sectionGuid The guid of the section that was dropped
 * @param sectionIndex The index of the section that was dropped
 */
const handleUpdateSectionChildren = async (
  sectionGuid: string,
  sectionIndex: number,
) => {
  const formGuid = form.value.guid;
  // get all the items after the dropped section until the element with the same parent guid
  let itemsToUpdateParentGuid = form.value.components.slice(sectionIndex + 1);

  for (let i = 0; i < itemsToUpdateParentGuid.length; i++) {
    if (itemsToUpdateParentGuid[i].parentGuid === formGuid) {
      itemsToUpdateParentGuid = itemsToUpdateParentGuid.slice(0, i);
      break;
    }
  }

  // Update the parent guid of all the items after the dropped section until the next section.
  for (const item of itemsToUpdateParentGuid) {
    item.parentGuid = sectionGuid;
  }
  // In draft mode the parent change persists on the next draft save (local mutation only).
  // Otherwise persist the elements that already exist server-side in ONE batched request
  // (new, guid-less items are saved when they're first created, not here).
  if (!isDraftMode.value) {
    const persisted = itemsToUpdateParentGuid.filter((item) => item.guid);
    if (persisted.length > 0) {
      try {
        await apiClient.formElement.updateFormElements(
          form.value.guid,
          persisted,
        );
      } catch (err) {
        showError("Error updating child Guids");
        console.log(err);
      }
    }
  }
};

const handleDropComponent = async (e: DragEvent, repeaterGuid?: string) => {
  if (!hasSetupBasicRole || !canEditForm.value) {
    e.preventDefault();
    return;
  }

  // we get the component from the pending component if it is set
  let droppedComponent = pendingComponent.value;
  pendingComponent.value = null;

  // if not, we get it from the dataTransfer
  if (droppedComponent === null) {
    const droppedComponentString = e.dataTransfer?.getData("text/plain") ?? "";
    droppedComponent = JSON.parse(droppedComponentString);
  }

  if (!droppedComponent) {
    showError("No pending component set");
    return;
  }

  const componentAboveId = (e.target as HTMLDivElement)?.id;
  // Id may be guid or questionCode

  let droppedIndex = form.value.components.findIndex(
    (c) => c.guid === componentAboveId,
  );

  if (droppedIndex === -1) {
    droppedIndex = form.value.components.findIndex(
      (c) => c.questionCode === componentAboveId,
    );
  }

  // handle new section
  if (
    droppedComponent.type === FormElementType.SECTION &&
    !droppedComponent.formDefinitionGuid
  ) {
    droppedComponent.formDefinitionGuid = form.value.guid;
    droppedComponent.parentGuid = form.value.guid;
    droppedComponent.questionCode = getNewQuestionCode(
      form.value,
      FormElementType.SECTION,
    );
    droppedComponent.label = splitByCaps(droppedComponent.questionCode);

    if (isDraftMode.value) {
      // Draft mode: no live element rows exist for the draft — assign the guid
      // client-side (children/parenting need it) and persist on draft save
      droppedComponent.guid = crypto.randomUUID();
      form.value.components.splice(droppedIndex + 1, 0, droppedComponent);
      ElMessage.success("Section added to draft");
    } else {
      // @ts-expect-error: Force the server to generate the Guid
      droppedComponent.guid = undefined;

      await apiClient.formElement
        .createFormElement(form.value.guid, droppedComponent)
        .then((formElement) => {
          droppedComponent.guid = formElement.guid;
          form.value.components.splice(droppedIndex + 1, 0, formElement);
          ElMessage.success("Section added to workflow");
        })
        .catch(showError);
    }

    editingComponent.value = form.value.components[droppedIndex + 1];

    // if the section was dropped in between another section
    if (
      form.value.components[droppedIndex + 2]?.parentGuid &&
      form.value.components[droppedIndex + 2]?.parentGuid !==
        droppedComponent.parentGuid
    ) {
      handleUpdateSectionChildren(droppedComponent.guid, droppedIndex + 1);
    }

    await handleSaveChanges();
    return;
  }

  if (
    droppedComponent.type === FormElementType.REPEATER &&
    !droppedComponent.formDefinitionGuid
  ) {
    droppedComponent.formDefinitionGuid = form.value.guid;
    droppedComponent.parentGuid = form.value.guid;
    droppedComponent.questionCode = getNewQuestionCode(
      form.value,
      FormElementType.REPEATER,
    );
    droppedComponent.label = splitByCaps(droppedComponent.questionCode);

    if (isDraftMode.value) {
      // Draft mode: assign the guid client-side and persist on draft save
      droppedComponent.guid = crypto.randomUUID();
      form.value.components.splice(droppedIndex + 1, 0, droppedComponent);
      ElMessage.success("Repeater added to draft");
    } else {
      // @ts-expect-error: Force the server to generate the Guid
      droppedComponent.guid = undefined;

      await apiClient.formElement
        .createFormElement(form.value.guid, droppedComponent)
        .then((res) => {
          droppedComponent.guid = res.guid;
          form.value.components.splice(droppedIndex + 1, 0, res);
          ElMessage.success("Repeater added to workflow");
        })
        .catch(showError);
    }

    editingComponent.value = form.value.components[droppedIndex + 1];
    await handleSaveChanges();
    return;
  }

  if (
    droppedComponent.type === FormElementType.ENTITY ||
    droppedComponent.type === FormElementType.WORKFLOW_ASSOCIATION
  ) {
    const allEntityDefinitions =
      await apiClient.entityDefinition.getAllEntityDefinitionDtos(
        myInfoStore.currentSysOrgGuid,
      );
    entityDefinitions.value = allEntityDefinitions.map((f) => ({
      label: f.name ?? "",
      value: f.guid ?? "",
    }));
  }

  if (droppedComponent.type === FormElementType.SECURITY_GROUP) {
    const allSecurityGroups = await apiClient.orgGroup.getAllOrgGroupDtos(
      myInfoStore.currentSysOrgGuid,
    );
    securityGroups.value = allSecurityGroups.map((f) => ({
      label: f.name ?? "",
      value: f.guid ?? "",
    }));
  }

  if (droppedComponent.type === FormElementType.LINKED_ANSWER) {
    const allForms = await apiClient.formDefinition.getAllFormDefinitions();
    formDefinitionsList.value = allForms.map((f) => ({
      label: f.name,
      value: f.guid,
    }));
  }

  // the parent guid of this component is the guid of the first section above it
  const parentGuid =
    repeaterGuid ??
    form.value.components
      .slice(0, droppedIndex + 1)
      .reverse()
      .find((c) => c.type === FormElementType.SECTION)?.guid;

  // if there is no section above it, then don't allow the drop
  if (!parentGuid && droppedComponent.type !== FormElementType.SECTION) {
    showError("A form item must be added to a Section.");
    return;
  }

  if (e.dataTransfer?.effectAllowed === "copy") {
    const newElement: FormElementBase = {
      type: droppedComponent.type,
      label: droppedComponent.label,
      properties: JSON.parse(JSON.stringify(droppedComponent.properties || {})),
      events: [],
      formDefinitionGuid: form.value.guid,
      // @ts-expect-error: Force the server to generate the Guid
      parentGuid: parentGuid,
      questionCode: getNewQuestionCode(form.value, droppedComponent.type),
      // @ts-expect-error: Force the server to generate the Guid
      guid: undefined, // force server to generate the guid
    };

    // if it is a compliance item, don't change the label
    newElement.label =
      newElement.type === FormElementType.COMPLIANCE_ITEM
        ? newElement.label
        : splitByCaps(newElement.questionCode);

    form.value.components.splice(droppedIndex + 1, 0, newElement);

    editingComponent.value = newElement;
  } else if (e.dataTransfer?.effectAllowed === "move") {
    // get the current index of the component that was dropped
    const currentIndex = form.value.components.findIndex(
      // assume questions have been saved and have guids
      (c) => c.guid === droppedComponent.guid,
    );

    // When you drag from a greater index to a smaller index, the droppedIndex
    // needs to be incremented by 1 because the target index is affected by the
    // shift caused by the removal of the component
    if (!repeaterGuid && droppedIndex < currentIndex) {
      droppedIndex++;
    }

    // if the dropped component is a section, move the section and its children
    if (droppedComponent.type === FormElementType.SECTION) {
      const sectionChildren = form.value.components.filter(
        (c) => c.parentGuid === droppedComponent.guid,
      );

      // remove the section and its children from the list
      form.value.components = form.value.components.filter(
        (c) => c.guid !== droppedComponent.guid && !sectionChildren.includes(c),
      );

      // add the section and its children to the new location
      form.value.components.splice(
        droppedIndex,
        0,
        droppedComponent,
        ...sectionChildren,
      );
    }
    // otherwise, move the component
    else {
      // update the parent guid of the dropped component in case it was moved to a different section
      // @ts-expect-error: Force the server to generate the Guid
      droppedComponent.parentGuid = parentGuid;

      // remove the dropped component from the list
      form.value.components = form.value.components.filter(
        (c) => c.questionCode !== droppedComponent.questionCode,
      );

      // add it to its new location
      form.value.components.splice(droppedIndex, 0, droppedComponent);
    }
  } else {
    showError(
      "`effectAllowed` on component drop has no handler: " +
        e.dataTransfer?.effectAllowed,
    );
  }

  pendingComponent.value = null;
};

const handleOnDrag = (e: DragEvent, component: FormElementBase) => {
  e.stopPropagation();

  if (!hasSetupBasicRole || !canEditForm.value) {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "none";
    }
    return;
  }

  // if the component is the first Page Break in a Section, don't allow it to be moved
  if (
    component.type === FormElementType.HEADING &&
    form.value.components[form.value.components.indexOf(component) - 1]
      ?.type === FormElementType.SECTION
  ) {
    e.dataTransfer?.setDragImage(new Image(), 0, 0);
    ElMessage.warning("This component cannot be moved");
    return;
  }

  // if the component is a repeater, don't allow it to be dropped in another repeater
  if (component.type === FormElementType.REPEATER) {
    canDropInRepeater.value = false;
  } else {
    canDropInRepeater.value = true;
  }

  // To fix chrome bug. See:
  // https://stackoverflow.com/questions/14203734/dragend-dragenter-and-dragleave-firing-off-immediately-when-i-drag#20733870
  setTimeout(() => {
    isDragging.value = e.type === "dragstart";
  }, 10);

  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = "move";
    pendingComponent.value = JSON.parse(JSON.stringify(component));
    canDropInRepeater.value = component.type !== FormElementType.REPEATER;
    // can't use dataTransfer.setData() because the tinyMCE editor will accept it and we don't want to drop there
    // e.dataTransfer.setData("text/plain", JSON.stringify(component));
  }
};

const dragDropState = useDragDrop({
  isDragging,
  handleDropComponent,
  handleClickComponent,
  handleOnDrag,
  editingComponent,
  canDropInRepeater,
});
provide(dragDropInjectKey, dragDropState);

const adjustSavedValues = (form: FormDefinition) => {
  form.components.forEach((c) => {
    switch (c.type) {
      case FormElementType.FORM_TEXT: {
        // the tinyMCE editor sometimes adds a trailing blank line, especially after an <hr>, which we don't want to save
        const trailingBlankLine = "\n<p>&nbsp;</p>";
        const el: FormTextFormElement = c as FormTextFormElement;
        let text = el.properties.content ?? "";
        let changeCount = 0;
        while (text.endsWith(trailingBlankLine)) {
          text = text.slice(0, 0 - trailingBlankLine.length);
          changeCount++;
        }
        if (changeCount) {
          // replace it
          el.properties.content = text;
        }
        break;
      }
    }
  });
  return form;
};

const refreshFormDefinition = async (updateInfo: any) => {
  const formattedTime = new Date(updateInfo.updatedAt).toLocaleTimeString();
  const currentConnectionId = formDesignerHub?.connectionId;
  const currentUserName = myInfoStore.userInfo?.loginName;
  const isSameUser = updateInfo.updatedByUser === currentUserName;
  const isSameConnection =
    !!updateInfo.updatedByConnectionId &&
    updateInfo.updatedByConnectionId === currentConnectionId;

  // Ignore only updates originating from this exact browser tab/connection.
  if (
    !isSameConnection &&
    (!isSameUser || !!updateInfo.updatedByConnectionId)
  ) {
    const loadingMsg = ElMessage({
      message: "Refreshing form definition...",
      type: "info",
      duration: 0,
    });

    try {
      let hasAnotherEditorPresent =
        !!updateInfo.updatedByConnectionId &&
        updateInfo.updatedByConnectionId !== currentConnectionId;

      const hasAnotherConnectionFromCache = Array.from(
        latestEditorConnectionIds.value,
      ).some((connectionId) => connectionId !== currentConnectionId);
      hasAnotherEditorPresent =
        hasAnotherEditorPresent || hasAnotherConnectionFromCache;

      if (
        !hasAnotherEditorPresent &&
        formDesignerHub?.state === "Connected" &&
        latestEditorConnectionIds.value.size === 0
      ) {
        const editorsSnapshot = await formDesignerHub.invoke(
          "GetCurrentEditors",
          updateInfo.formDefinitionGuid,
        );

        const users: any[] = editorsSnapshot?.users ?? [];
        const hasAnotherConnectionFromSnapshot = users.some(
          (u: any) => u?.connectionId !== currentConnectionId,
        );

        hasAnotherEditorPresent =
          hasAnotherEditorPresent || hasAnotherConnectionFromSnapshot;
      }

      // In draft mode a collaborator's save went to the draft — reload the draft,
      // not the live definition
      let updatedForm: FormDefinition | undefined;
      if (isDraftMode.value) {
        const draftResult = await apiClient.formDefinition.createOrResumeDraft(
          updateInfo.formDefinitionGuid,
        );
        draftUpdatedAt.value = draftResult.draftUpdatedAt;
        updatedForm = draftResult.draft;
      } else {
        updatedForm = await apiClient.formDefinition.getFormDefinition(
          updateInfo.formDefinitionGuid,
        );
      }
      if (updatedForm) {
        loadingMsg.close();
        form.value = updatedForm;
        // Clear the history after form is updated from another user
        nextTick(() => history.clear());

        const txt2 = hasAnotherEditorPresent
          ? `The form has been updated by ${updateInfo.updatedByUser} at ${formattedTime}.`
          : "The form has been updated.";

        ElMessage({
          message: txt2,
          type: "success",
          duration: getMessageTime(txt2),
        });
      }
    } catch (error) {
      showError(
        "Failed to refresh form definition. Please try refreshing the page manually.",
        error,
      );
    } finally {
      loadingMsg.close();
    }
  }
};

const handleCancelChanges = async () => {
  if (history.history.value.length > 1) {
    try {
      await ElMessageBox.confirm(
        "Are you sure you want to discard your changes?",
        "Discard Changes",
        {
          cancelButtonText: "Continue Editing",
          confirmButtonText: "Discard",
          closeOnPressEscape: false,
          type: "warning",
        },
      );
    } catch {
      return;
    }
    if (form.value.guid) {
      try {
        // In draft mode, revert to the draft's last saved state (not the live form)
        const latestForm = isDraftMode.value
          ? (
              await apiClient.formDefinition.createOrResumeDraft(
                form.value.guid,
              )
            ).draft
          : await apiClient.formDefinition.getFormDefinition(form.value.guid);
        if (latestForm) {
          form.value.components = latestForm.components;
        }
      } catch (err) {
        showError("Failed to reload form from server.", err);
      }
    }
    history.clear();
  }
};

const handleSaveChanges = async () => {
  clearTimeout(saveChangesId.value);
  // are there any component with the same question code?
  const questionCodes = form.value.components.map((c) => c.questionCode);
  const duplicates = questionCodes.filter(
    (code, index) => questionCodes.indexOf(code) !== index,
  );
  if (duplicates.length > 0) {
    showError(
      `There are duplicate question codes: ${duplicates.join(", ")}. Please fix these before saving.`,
    );
    return;
  }

  const adjusted = adjustSavedValues(form.value);

  // Issue #1015: if only individual element bodies changed (no rules/scalars, no
  // add/remove, no reorder), persist just those elements — a scoped save. Otherwise fall
  // back to the whole-form/whole-draft save. Applies in both draft and not-in-use modes.
  const canScopeToElements =
    !nonElementPartsChanged(adjusted) && !elementOrderChanged(adjusted);
  const elementsToSave = canScopeToElements ? changedElements(adjusted) : [];

  let savePromise: Promise<FormDefinition>;
  if (canScopeToElements && isDraftMode.value) {
    // One request for all changed elements — a single scoped save and a single history entry.
    savePromise = apiClient.formDefinition
      .updateDraftElements(form.value.guid, elementsToSave)
      .then((result) => {
        draftUpdatedAt.value = result.draftUpdatedAt;
        draftSavedThisSession.value = true;
        draftHasSavedChanges.value = true;
        return result.draft;
      });
  } else if (canScopeToElements && !isDraftMode.value) {
    // One request for all changed elements — a single scoped save and a single history entry.
    savePromise = apiClient.formElement
      .updateFormElements(form.value.guid, elementsToSave)
      .then(() => adjusted);
  } else if (isDraftMode.value) {
    // Whole-draft save (rules/scalars/rename ripple or structural change)
    savePromise = apiClient.formDefinition
      .updateDraft(form.value.guid, adjusted)
      .then((draftResult) => {
        draftUpdatedAt.value = draftResult.draftUpdatedAt;
        draftSavedThisSession.value = true;
        draftHasSavedChanges.value = true;
        return draftResult.draft;
      });
  } else {
    savePromise = apiClient.formDefinition.updateFormDefinition(
      form.value.guid,
      adjusted,
    );
  }

  await savePromise
    .then((res) => {
      justReturned.value = true;
      if (!formDesignerHub) {
        // Adopt the server response for a whole-form save (it may assign guids to new
        // children) before snapshotting the baseline, so later scoped-save diffs compare
        // against the persisted state — same as the hub-connected path below.
        if (!canScopeToElements) {
          form.value = res;
        }
        snapshotSavedState(form.value);
        emit("saveNeeded", false);
        return;
      }

      // For a scoped element save the shared reactive model is already current, so we keep
      // it (avoiding a full-form replace + preview remount). For a whole-form save we adopt
      // the server response (it may assign guids to new children).
      if (!canScopeToElements) {
        form.value = res;
        // Reset the guid to the server returned one if we have a new element selected
        if (_editingComponentGuid.value === undefined) {
          _editingComponentGuid.value = res.components.find(
            (c) => c.questionCode === editingComponent.value?.questionCode,
          )?.guid;
        }
      }

      nextTick(() => history.clear());
      snapshotSavedState(form.value);
      const txt = isDraftMode.value ? "Draft saved" : "Saved";
      ElMessage.success({
        message: txt,
        grouping: true,
        duration: getMessageTime(txt),
      });
      // Only a whole-form save needs the preview remount; a scoped element save is already
      // reflected in the shared reactive model.
      if (!canScopeToElements) {
        lastSaveTime.value = new Date();
      }
      emit("formSaved", res);
      emit("saveNeeded", false);

      if (formDesignerHub?.state === "Connected") {
        formDesignerHub
          .invoke(
            "NotifyFormUpdated",
            form.value.guid,
            myInfoStore.userInfo?.loginName ?? "",
          )
          .catch((err) => console.warn("NotifyFormUpdated failed", err));
      }
    })
    .catch(async (error) => {
      if (isDraftMode.value && error?.status === 404) {
        // The draft was committed or discarded by someone else — reload
        showError(
          "This draft no longer exists — another user committed or discarded it. Reloading the form.",
          error,
        );
        await reloadFormAndDraftState();
        return;
      }
      if (!isDraftMode.value && error?.status === 409) {
        // The form became in-use since it was loaded — switch to draft editing.
        // Local changes stay in the model; save again to store them in the draft.
        showError(
          "This form is now in use, so edits must go through a draft. Your changes were kept — save again to store them in the draft.",
          error,
        );
        requiresDraft.value = true;
        const resolution = await resolveEditableForm(
          { ...form.value, requiresDraft: true },
          { silentResume: true },
        ).catch((err) => {
          showError("Failed to open the form's draft.", err);
          return undefined;
        });
        if (resolution?.isDraftMode) {
          // Keep the user's local edits (same guid; element guids are unchanged)
          isDraftMode.value = true;
          draftUpdatedAt.value = resolution.draftUpdatedAt;
        }
        return;
      }
      showError(error);
    });
};

/** Reloads the live form and re-resolves the draft state (used after a draft vanishes). */
const reloadFormAndDraftState = async () => {
  try {
    const live = await apiClient.formDefinition.getFormDefinition(
      form.value.guid,
    );
    form.value = live;
    nextTick(() => history.clear());
    await resolveDraftState();
  } catch (error) {
    showError("Failed to reload the form. Please refresh the page.", error);
  }
};

/**
 * Suppresses our own DraftCommitted/DraftDiscarded hub echoes for a few seconds —
 * the events arrive over the socket after the HTTP call returns.
 */
const suppressDraftEventsBriefly = () => {
  suppressDraftEvents.value = true;
  setTimeout(() => (suppressDraftEvents.value = false), 3000);
};

/** "Edit as Draft": creates/resumes the draft and unlocks editing. */
const handleEnterDraft = async () => {
  await enterDraftMode();
};

/**
 * Leaves draft mode back to the read-only live view and loads the given (committed or
 * live) definition. Cleans up local editing state without eagerly creating a new draft.
 */
const returnToReadOnly = (liveForm: FormDefinition) => {
  isDraftMode.value = false;
  draftUpdatedAt.value = undefined;
  draftSavedThisSession.value = false;
  editingComponent.value = undefined;
  form.value = liveForm;
  // Keep requiresDraft true (the form is still in use) unless the payload says
  // otherwise. canEditForm (computed) then keeps in-use forms locked until the user
  // clicks "Edit as Draft" again.
  requiresDraft.value = liveForm.requiresDraft ?? requiresDraft.value;
  nextTick(() => history.clear());
};

/** Commits the draft to the live form, then returns to the read-only view. */
const handleCommitDraft = async () => {
  // Confirm before saving/spinning so the button doesn't spin behind the dialog
  if (!(await confirmCommitDraft())) {
    return;
  }
  committingDraft.value = true;
  suppressDraftEventsBriefly();
  try {
    if (saveNeeded.value) {
      await handleSaveChanges();
    }
    const committed = await commitDraft(form.value.guid, draftUpdatedAt.value);
    if (!committed) {
      return;
    }
    emit("formSaved", committed);
    // committed is the live definition; RequiresDraft stays true while it's in use
    returnToReadOnly(committed);
  } finally {
    committingDraft.value = false;
  }
};

/** Discards the draft and returns to the read-only live view. */
const handleDiscardDraft = async () => {
  if (!(await confirmDiscardDraft())) {
    return;
  }
  discardingDraft.value = true;
  suppressDraftEventsBriefly();
  try {
    const discarded = await discardDraft(form.value.guid);
    if (!discarded) {
      return;
    }
    const live = await apiClient.formDefinition.getFormDefinition(
      form.value.guid,
    );
    returnToReadOnly(live);
  } catch (error) {
    showError("Failed to reload the form. Please refresh the page.", error);
  } finally {
    discardingDraft.value = false;
  }
};

/** Another designer committed this form's draft — reload onto the fresh state. */
const handleDraftCommitted = async (info: any) => {
  if (suppressDraftEvents.value) {
    return;
  }
  const msg = `${info?.byUser ?? "Another user"} committed this form's draft. Reloading.`;
  ElMessage.warning({ message: msg, duration: getMessageTime(msg) });
  await reloadFormAndDraftState();
};

/** Another designer discarded this form's draft — reload onto the live state. */
const handleDraftDiscarded = async (info: any) => {
  if (suppressDraftEvents.value || !isDraftMode.value) {
    return;
  }
  const msg = `${info?.byUser ?? "Another user"} discarded this form's draft. Reloading.`;
  ElMessage.warning({ message: msg, duration: getMessageTime(msg) });
  await reloadFormAndDraftState();
};

const handleStopEditing = () => {
  editingComponent.value = undefined;
};

const handleDeleteElementFromWorkflow = async (element: FormElementBase) => {
  const referencedRules: FormElementRule[] = form.value.rules.filter(
    (r) =>
      r.elementQuestionCode === element.questionCode ||
      r.conditions.some((c) =>
        isFormElementBasedCondition(c)
          ? c.questionCode === element.questionCode
          : false,
      ),
  );

  let deleteMessage: string;
  if (referencedRules.length > 0) {
    deleteMessage = `This will permanently delete this element, any answers associated with it, and any rules/conditions that reference it. Continue?`;
  } else {
    deleteMessage = `This will permanently delete this element and any answers associated with it. Continue?`;
  }

  const title = `Deleting: ${element.label}`;

  try {
    await ElMessageBox.confirm(deleteMessage, title, {
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      type: "warning",
    });

    editingComponent.value = undefined;

    // Remove rules referencing this element
    let rules = form.value.rules;
    if (referencedRules.length > 0) {
      rules = rules.filter(
        (r) => r.elementQuestionCode !== element.questionCode,
      );
      rules = rules.map((r) => ({
        ...r,
        conditions: r.conditions.filter((c) =>
          isFormElementBasedCondition(c)
            ? c.questionCode !== element.questionCode
            : true,
        ),
      }));
      rules = rules.filter((r) => r.conditions.length !== 0);
    }
    form.value.rules = rules;

    // Actually delete the element and its answers.
    // In draft mode the live element row must not be touched — the removal is applied
    // (delete or archive, per server rules) when the draft is committed.
    if (element.guid && !isDraftMode.value) {
      try {
        await apiClient.formElement.deleteFormElement(
          element.guid,
          form.value.guid,
        );
      } catch (error) {
        showError("Failed to delete element and answers from server.", error);
        return;
      }
    }

    // Remove from local components
    form.value.components = form.value.components.filter(
      (c) => c.guid !== element.guid,
    );

    ElMessage.warning({
      message: isDraftMode.value
        ? `${element.questionCode} will be removed when the draft is committed`
        : `${element.questionCode} and its answers will be deleted on next Save`,
    });
  } catch {
    // Cancelled
  }
};

const handleArchiveElementFromWorkflow = async (element: FormElementBase) => {
  const referencedRules: FormElementRule[] = form.value.rules.filter(
    (r) =>
      r.elementQuestionCode === element.questionCode ||
      // r.conditions.some((c) => c.questionCode === element.questionCode),
      r.conditions.some((c) =>
        isFormElementBasedCondition(c)
          ? c.questionCode === element.questionCode
          : false,
      ),
  );

  let archiveMessage: string;
  if (referencedRules.length > 0) {
    archiveMessage = `This will archive this element and any rules/conditions that reference it. Continue?`;
  } else {
    archiveMessage = `This will archive this element. Continue?`;
  }

  if (responsesExist.value) {
    archiveMessage =
      "This will archive this element and it will no longer show in any responses. Continue?";
  }

  const title = `Archiving: ${element.label}`;

  ElMessageBox.confirm(archiveMessage, title, {
    confirmButtonText: "OK",
    cancelButtonText: "Cancel",
    type: "warning",
  }).then(() => {
    editingComponent.value = undefined;

    // if the element has responses, we archive it
    if (!responsesExist.value) {
      // If the component has a guid, it has been saved to the db, so we should delete it there
      let rules = form.value.rules;
      if (referencedRules.length > 0) {
        // Delete rules that directly reference this element
        rules = rules.filter(
          (r) => r.elementQuestionCode !== element.questionCode,
        );
        // Remove all conditions that directly reference this element
        rules = rules.map((r) => ({
          ...r,
          conditions: r.conditions.filter((c) =>
            isFormElementBasedCondition(c)
              ? c.questionCode !== element.questionCode
              : true,
          ),
        }));
        // Remove all rules that no longer have any conditions
        rules = rules.filter((r) => r.conditions.length !== 0);
      }

      form.value.rules = rules;
      form.value.components = form.value.components.filter(
        (c) => c !== element,
      );
    } else {
      element.isArchived = true;
      form.value.components = form.value.components.map((c) =>
        c.guid === element.guid ? element : c,
      );
    }
    ElMessage.warning({
      message: `${element.questionCode} will be ${responsesExist.value ? "archived" : "deleted"} on next Save`,
    });
  });
};

const handleUnarchiveElementFromWorkflow = async (element: FormElementBase) => {
  const msg =
    "This will unarchive this element and it will now appear in any responses. Continue?";

  ElMessageBox.confirm(msg, `Unarchiving: ${element.label}`, {
    confirmButtonText: "OK",
    cancelButtonText: "Cancel",
    type: "warning",
  }).then(() => {
    editingComponent.value = undefined;
    element.isArchived = false;
    form.value.components = form.value.components.map((c) =>
      c.guid === element.guid ? element : c,
    );
    ElMessage.warning({
      message: `${element.questionCode} will be unarchived on next Save`,
    });
  });
};

const handleOpenRuleDrawer = () => {
  isRuleDrawerOpen.value = true;
};
const handleCloseRuleDrawer = () => {
  isRuleDrawerOpen.value = false;
};

/**
 * Updates the form when the FormPropertiesEditorModal is saved
 * @param updated The updated form
 */
const handleUpdateFormProperties = (updated: FormDefinition) => {
  form.value = updated;
  // Clear the current history so we don't prompt for another save
  nextTick(history.clear);
};

const confirmLeave = async (): Promise<boolean> => {
  return ElMessageBox.confirm("You have unsaved changes.", "Unsaved Changes", {
    cancelButtonText: "Discard",
    confirmButtonText: "Continue Editing",
    closeOnPressEscape: false,
    type: "warning",
  })
    .then(() => {
      return false; // don't close
    })
    .catch(() => {
      return true;
    });
};

// Invoke the LeaveGroup method from the server to leave the group and update userlist
const leaveFormGroup = () => {
  // Best-effort cleanup: leaving a draft that was never saved (and has no other
  // editors) removes it, so merely opening a form doesn't leave a draft behind.
  // The server double-checks the draft is pristine before deleting.
  if (
    isDraftMode.value &&
    !draftSavedThisSession.value &&
    !hasOtherActiveEditors.value
  ) {
    discardDraftIfPristine(form.value.guid);
  }

  if (formDesignerHub && formDesignerHub.state === "Connected") {
    try {
      formDesignerHub.invoke("LeaveGroup", form.value.guid);
      if (myInfoStore.userInfo) {
        ElMessage.closeAll();
      }
    } catch (error) {
      showError("Error leaving form group:", error);
    }
  }
};

onBeforeRouteLeave(async () => {
  let confirm = true;
  if (saveNeeded.value) {
    confirm = await confirmLeave();
  }
  return confirm;
});

// Save form automatically, debounced with a delay of 5000ms
const saveChangesId = ref<ReturnType<typeof setTimeout> | undefined>(undefined);
const justReturned = ref<boolean>(false);
watch(
  () => form,
  async () => {
    if (!autoSaveForm.value) {
      return;
    }
    if (justReturned.value) {
      justReturned.value = false;
      return;
    }

    if (saveChangesId.value !== undefined) {
      clearTimeout(saveChangesId.value);
    }
    saveChangesId.value = setTimeout(() => handleSaveChanges(), 5000);
  },
  { deep: true },
);

watch(
  () => saveNeeded.value,
  (saveNeeded) => {
    emit("saveNeeded", saveNeeded);
  },
);

// Refresh the scoped-save baseline whenever the undo history is cleared (which happens
// after every load, draft resolution, reload, and save). At that moment the form matches
// what's persisted, so it's the correct "last saved" snapshot to diff future edits against.
watch(
  () => history.history.value.length,
  (len) => {
    if (len <= 1) {
      snapshotSavedState(form.value);
    }
  },
  { immediate: true },
);

const securityGroups = ref<LabelledOption[]>([]);
const entityDefinitions = ref<LabelledOption[]>([]);
const formDefinitionsList = ref<LabelledOption[]>([]);
const listDefinitionsList = ref<LabelledOption[]>([]);
const eventCodesList = ref<LabelledOption[]>([]);

const myInfoStore = useMyInfoStore();
const sectionAssignables = ref<SectionAssignables>({});
const responsesExist = ref<boolean>();

const hasSetupBasicRole = myInfoStore.hasSetupBasicRole;

let formDesignerHub: HubConnection | null;

onMounted(async () => {
  // console.log("FormDesignerPage mounted", { drawerClosed: props.drawerClosed });

  // Check if drawer is already closed on mount and call leaveFormGroup if needed
  if (props.drawerClosed) {
    leaveFormGroup();
  }

  // get the list here... could do at a higher level, but not too bad here
  const extras = await apiClient.formDesignerExtras.getFormDesignerExtras(
    myInfoStore.currentSysOrgGuid,
  );
  sectionAssignables.value = extras.sectionAssignables;
  responsesExist.value = await apiClient.formResponses.hasResponses(
    emptyGuid,
    form.value.guid,
  );
  window.addEventListener("keydown", handleKeyDown);

  if (
    form.value.components.some(
      (c) =>
        c.type === FormElementType.ENTITY ||
        c.type === FormElementType.WORKFLOW_ASSOCIATION,
    )
  ) {
    const allEntityDefinitions =
      await apiClient.entityDefinition.getAllEntityDefinitionDtos(
        myInfoStore.currentSysOrgGuid,
      );
    entityDefinitions.value = allEntityDefinitions.map((f) => ({
      label: f.name ?? "",
      value: f.guid ?? "",
    }));
  }

  if (
    form.value.components.some((c) => c.type === FormElementType.SECURITY_GROUP)
  ) {
    const allSecurityGroups = await apiClient.orgGroup.getAllOrgGroupDtos(
      myInfoStore.currentSysOrgGuid,
    );

    securityGroups.value = allSecurityGroups.map((f) => ({
      label: f.name ?? "",
      value: f.guid ?? "",
    }));
  }

  if (
    form.value.components.some((c) => c.type === FormElementType.LINKED_ANSWER)
  ) {
    const allForms = await apiClient.formDefinition.getAllFormDefinitions();
    formDefinitionsList.value = allForms.map((f) => ({
      label: f.name,
      value: f.guid,
    }));
  }

  const allLists = await apiClient.listDefinition.getPdfCodeListDefinitions(
    props.workflowDefinition?.guid,
    myInfoStore.currentSysOrgGuid,
  );
  listDefinitionsList.value = allLists.flatMap((f) =>
    f.items.map((i) => ({
      label: i.label,
      value: i.value,
    })),
  );

  eventCodesList.value =
    props.workflowDefinition?.statusChangeActionsList
      .filter(
        (action) => action.triggerType === WorkflowEventTriggerType.ACTION_CODE,
      )
      .map((action) => ({
        label: action.eventCode ?? "",
        value: action.eventCode ?? "",
      })) || [];
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleKeyDown);
  clearTimeout(hubResponseTimeout.value);

  leaveFormGroup();
  // Use handler refs (not bare off) to avoid stripping listeners from other "formdesigner" hub users.
  formDesignerHub?.off("FormEditors", handleFormDesignerHubMessage);
  formDesignerHub?.off("FormUpdated", refreshFormDefinition);
  formDesignerHub?.off("DraftCommitted", handleDraftCommitted);
  formDesignerHub?.off("DraftDiscarded", handleDraftDiscarded);
  formDesignerHub?.off("ControlRequestReceived", handleControlRequestReceived);
  formDesignerHub?.off("ControlRequestResponse", handleControlRequestResponse);
  formDesignerHub?.off("JoinGroupError", handleJoinGroupError);

  // clear the list definitions from session storage since they are form-specific
  sessionStorage.removeItem("listDefinitions");
});

// Leave the group when the drawer closes
watch(
  isDrawerClosed,
  (val) => {
    if (val) {
      clearTimeout(hubResponseTimeout.value);
      leaveFormGroup();
      formDesignerHub?.off("FormEditors", handleFormDesignerHubMessage);
      formDesignerHub?.off("FormUpdated", refreshFormDefinition);
      formDesignerHub?.off("DraftCommitted", handleDraftCommitted);
      formDesignerHub?.off("DraftDiscarded", handleDraftDiscarded);
      formDesignerHub?.off(
        "ControlRequestReceived",
        handleControlRequestReceived,
      );
      formDesignerHub?.off(
        "ControlRequestResponse",
        handleControlRequestResponse,
      );
      formDesignerHub?.off("JoinGroupError", handleJoinGroupError);
      if (myInfoStore.userInfo) {
        ElMessage.closeAll();
      }
    }
  },
  { immediate: true },
);

watch(
  isDrawerOpen,
  async (val) => {
    if (!val) return;

    formDesignerHub = await hubManager.getHub("formdesigner", "/formdesigner");
    if (!formDesignerHub) {
      // Hub failed to connect — allow editing based on role
      collaborationAllowsEdit.value = hasSetupBasicRole;
      return;
    }

    const msg = ElMessage({
      message: "Connecting to form designer hub...",
      type: "warning",
      duration: 0,
    });

    // Off first using stable refs (in case of re-open without full unmount) — only our listeners.
    formDesignerHub.off("FormEditors", handleFormDesignerHubMessage);
    formDesignerHub.off("FormUpdated", refreshFormDefinition);
    formDesignerHub.off("DraftCommitted", handleDraftCommitted);
    formDesignerHub.off("DraftDiscarded", handleDraftDiscarded);
    formDesignerHub.off("ControlRequestReceived", handleControlRequestReceived);
    formDesignerHub.off("ControlRequestResponse", handleControlRequestResponse);
    formDesignerHub.off("JoinGroupError", handleJoinGroupError);

    formDesignerHub.on("FormEditors", handleFormDesignerHubMessage);
    formDesignerHub.on("FormUpdated", refreshFormDefinition);
    formDesignerHub.on("DraftCommitted", handleDraftCommitted);
    formDesignerHub.on("DraftDiscarded", handleDraftDiscarded);
    formDesignerHub.on("ControlRequestReceived", handleControlRequestReceived);
    formDesignerHub.on("ControlRequestResponse", handleControlRequestResponse);
    formDesignerHub.on("JoinGroupError", handleJoinGroupError);

    try {
      await formDesignerHub.invoke(
        "JoinGroup",
        form.value.guid,
        myInfoStore.userInfo?.loginName ?? "",
      );

      const snapshot = await formDesignerHub.invoke(
        "GetCurrentEditors",
        form.value.guid,
      );
      if (snapshot) {
        handleFormDesignerHubMessage(snapshot);
      }

      // If no FormEditors response within 2s, fall back to role-based editing
      hubResponseTimeout.value = setTimeout(() => {
        if (!collaborationAllowsEdit.value && hasSetupBasicRole) {
          collaborationAllowsEdit.value = true;
        }
        msg.close();
      }, 2000);
    } catch (error) {
      msg.close();
      showError("Error joining form group:", error);
      collaborationAllowsEdit.value = hasSetupBasicRole;
    }
  },
  { immediate: true },
);

/**
 * Whether the real-time collaboration layer (concurrency mode + other editors) grants
 * this tab edit control. Driven entirely by the hub. The user's *effective* ability to
 * edit is `canEditForm`, which also respects the draft lock below.
 */
const collaborationAllowsEdit = ref<boolean>(false);

/**
 * True when editing is blocked because the form is in use and the user hasn't entered
 * draft mode yet (as opposed to being blocked by another concurrent editor).
 */
const isDraftLockActive = computed<boolean>(
  () => requiresDraft.value && !isDraftMode.value,
);

/**
 * Effective editability. An in-use form is locked until the user enters draft mode
 * ("Edit as Draft"), regardless of what the collaboration layer says.
 */
const canEditForm = computed<boolean>(() => {
  if (isDraftLockActive.value) {
    return false;
  }
  return collaborationAllowsEdit.value;
});
const controlRequestPending = ref(false);
const hasOtherActiveEditors = ref(false);
const latestEditorConnectionIds = ref<Set<string>>(new Set<string>());
/** Timeout ID for the hub response fallback */
const hubResponseTimeout = ref<ReturnType<typeof setTimeout> | undefined>(
  undefined,
);

/*
 * Editing the form *definition* in the designer always uses Request Control: the first
 * editor holds control and others must request it. The form's own
 * `metaInfo.concurrencyMode` only governs concurrent *answering* in the renderer
 * (FormRendererInner.vue) — it deliberately does not affect designer editing.
 */
const concurrencyMode = computed(() => FormConcurrencyMode.REQUEST_CONTROL);

const handleJoinGroupError = () => {
  collaborationAllowsEdit.value = hasSetupBasicRole;
};

const handleFormDesignerHubMessage = (formDesignerUsers: any) => {
  // Hub responded — clear the fallback timeout
  clearTimeout(hubResponseTimeout.value);

  const allConnections: any[] = formDesignerUsers.users ?? [];
  latestEditorConnectionIds.value = new Set(
    allConnections
      .map((u: any) => u?.connectionId)
      .filter((connectionId: string | undefined): connectionId is string =>
        Boolean(connectionId),
      ),
  );

  if (allConnections.length === 0) {
    collaborationAllowsEdit.value = hasSetupBasicRole;
    hasOtherActiveEditors.value = false;
    return;
  }

  const currentUserName = myInfoStore.userInfo?.loginName;
  const currentConnectionId = formDesignerHub?.connectionId;

  const currentConnection =
    allConnections.find((u) => u.connectionId === currentConnectionId) ?? null;

  // All connections except this browser tab/connection.
  // If connection id isn't available yet, fall back to username-based split.
  const otherConnections = allConnections.filter((u) =>
    currentConnectionId
      ? u.connectionId !== currentConnectionId
      : u.userName !== currentUserName,
  );

  hasOtherActiveEditors.value = otherConnections.some((u: any) => !!u.canEdit);

  // Deduplicate other users by name — for display only
  const seen = new Set<string>();
  const otherUsersDisplay = otherConnections.filter((u) => {
    if (seen.has(u.userName)) return false;
    seen.add(u.userName);
    return true;
  });
  const otherUserNames = otherUsersDisplay.map((u: any) => u.userName);

  // Current connection for this tab; fallback to earliest same-username connection.
  const myConnections = allConnections.filter(
    (u) => u.userName === currentUserName,
  );
  const currentUser =
    currentConnection ??
    myConnections.reduce(
      (earliest: any, u: any) =>
        !earliest || new Date(u.joinedAt) < new Date(earliest.joinedAt)
          ? u
          : earliest,
      null,
    );

  // No other users — current user has full access
  if (otherConnections.length === 0) {
    collaborationAllowsEdit.value = hasSetupBasicRole;
    controlRequestPending.value = false;
    hasOtherActiveEditors.value = false;
    ElMessage.closeAll();
    return;
  }

  if (!currentUser?.joinedAt) return;

  // Determine if current user can edit based on concurrency mode
  if (hasSetupBasicRole) {
    switch (concurrencyMode.value) {
      case FormConcurrencyMode.READ_ONLY_WHEN_SHARED:
        collaborationAllowsEdit.value = false;
        break;
      case FormConcurrencyMode.ALLOW_CONCURRENT:
        collaborationAllowsEdit.value = true;
        break;
      case FormConcurrencyMode.REQUEST_CONTROL:
      case FormConcurrencyMode.FIRST_IN_WINS:
      default: {
        const editorsWithPermission = otherConnections.filter(
          (u: any) => u.canEdit,
        );
        if (editorsWithPermission.length === 0) {
          collaborationAllowsEdit.value = true;
        } else {
          const currentJoined = new Date(currentUser.joinedAt);
          const isEarliest = editorsWithPermission.every(
            (u: any) => !u.joinedAt || new Date(u.joinedAt) > currentJoined,
          );
          collaborationAllowsEdit.value = isEarliest;
        }
        break;
      }
    }
  } else {
    collaborationAllowsEdit.value = false;
  }

  // Reset pending request if we gained control
  if (collaborationAllowsEdit.value) {
    controlRequestPending.value = false;
  }

  ElMessage.closeAll(); // clear previous messages

  // Build status message based on concurrency mode
  let message = "";
  let messageType: "warning" | "info" = "warning";

  if (concurrencyMode.value === FormConcurrencyMode.READ_ONLY_WHEN_SHARED) {
    message = buildOtherUsersMessage(otherUserNames);
    message += ". Form is read-only while other users are present";
    messageType = "info";
  } else if (concurrencyMode.value === FormConcurrencyMode.ALLOW_CONCURRENT) {
    message = buildOtherUsersMessage(otherUserNames);
    message += ". Concurrent editing is enabled";
    messageType = "info";
  } else {
    // First-in-wins or Request Control — determine who the controlling editor is
    // Use all connections to find the true earliest canEdit connection
    const connectionsWithEdit = allConnections.filter((u: any) => u.canEdit);
    const eligibleConnections =
      connectionsWithEdit.length > 0 ? connectionsWithEdit : allConnections;

    const editorConnection = eligibleConnections.reduce(
      (earliest: any, u: any) =>
        !earliest ||
        (u.joinedAt && new Date(u.joinedAt) < new Date(earliest.joinedAt))
          ? u
          : earliest,
      null,
    );

    if (
      (currentConnectionId &&
        editorConnection?.connectionId === currentConnectionId) ||
      (!currentConnectionId && editorConnection?.userName === currentUserName)
    ) {
      // Current user is the editor — just list who else is watching
      message = buildOtherUsersMessage(otherUserNames);
    } else if (editorConnection) {
      // Someone else is the editor
      const others = otherUsersDisplay.filter(
        (u: any) => u.userName !== editorConnection.userName,
      );
      const otherNames = others.map((u: any) => u.userName);
      const action = editorConnection.canEdit ? "editing" : "viewing";

      message = `${editorConnection.userName} is ${action} this form`;
      if (others.length === 1) {
        message += `. ${otherNames[0]} is also viewing the form`;
      } else if (others.length === 2) {
        message += `. ${otherNames[0]} and ${otherNames[1]} are also viewing the form`;
      } else if (others.length > 2) {
        const last = otherNames.pop();
        message += `. ${otherNames.join(", ")} and ${last} are also viewing the form`;
      }

      if (
        concurrencyMode.value === FormConcurrencyMode.REQUEST_CONTROL &&
        hasSetupBasicRole &&
        !canEditForm.value
      ) {
        message += ". Click 'Request Control' to request edit access";
      }
    }
  }

  ElMessage({
    message,
    type: messageType,
    duration: 0,
    showClose: true,
  });
};

/** Helper to build a message listing other users viewing the form */
const buildOtherUsersMessage = (otherUserNames: string[]): string => {
  if (otherUserNames.length === 1) {
    return `${otherUserNames[0]} is viewing this form`;
  } else if (otherUserNames.length === 2) {
    return `${otherUserNames[0]} and ${otherUserNames[1]} are viewing this form`;
  } else {
    const names = [...otherUserNames];
    const last = names.pop();
    return `${names.join(", ")} and ${last} are viewing this form`;
  }
};

/** Sends a request to the current editor to transfer control */
const handleRequestControl = async () => {
  if (!formDesignerHub || controlRequestPending.value) return;
  controlRequestPending.value = true;
  try {
    await formDesignerHub.invoke("RequestFormControl", form.value.guid);
  } catch (error) {
    controlRequestPending.value = false;
    showError("Failed to send control request", error);
  }
};

/** Current editor receives a control request from another user */
const handleControlRequestReceived = async (request: any) => {
  try {
    await ElMessageBox.confirm(
      `${request.requestingUserName} is requesting edit control of this form. Grant access?`,
      "Control Request",
      {
        confirmButtonText: "Grant",
        cancelButtonText: "Deny",
        type: "info",
      },
    );
    // Approved
    await formDesignerHub?.invoke(
      "RespondToControlRequest",
      form.value.guid,
      request.requestingConnectionId,
      true,
    );
  } catch {
    // Denied (or dialog cancelled)
    await formDesignerHub?.invoke(
      "RespondToControlRequest",
      form.value.guid,
      request.requestingConnectionId,
      false,
    );
  }
};

/** Requester receives the response to their control request */
const handleControlRequestResponse = (response: any) => {
  controlRequestPending.value = false;
  if (response.approved) {
    ElMessage.success("Control request approved");
  } else {
    ElMessage.info(`Control request denied by ${response.fromUserName}`);
  }
};

const showTipsDialog = ref<boolean>(false);
const handleKeyDown = (e: KeyboardEvent) => {
  if (e.ctrlKey || e.altKey) {
    return;
  }
  const element = e.target as HTMLElement;
  const tagName = element.tagName;
  const normalView = tagName === "BODY" || tagName === "BUTTON";

  switch (e.key) {
    case "/":
    case "?":
      if (normalView) {
        showTipsDialog.value = !showTipsDialog.value;
      }
      break;
    case "q":
      if (normalView) {
        showQuestionCodes.value = !showQuestionCodes.value;
      }
      break;
    case "r":
      if (normalView) {
        isRuleDrawerOpen.value = !isRuleDrawerOpen.value;
      }
      break;
    case "Escape":
      // close the properties editor when the escape key is pressed
      editingComponent.value = undefined;
      break;
  }
};

// const handleDownloadFormJson = () => {
//   const element = document.createElement("a");
//   const file = new Blob([JSON.stringify(form.value, null, 2)], {
//     type: "text/plain",
//   });
//   element.href = URL.createObjectURL(file);
//   element.download = `${form.value.name}.json`;
//   document.body.appendChild(element); // Required for this to work in FireFox
//   element.click();
// };
// const handleUploadFormJson = () => {
//   uploaderOpenCounter.value++;
//   formUploaderVisible.value = true;
// };

const pendingComponent = ref<FormElementBase | null>(null);
const handlePendingComponent = (
  component: FormElementDefault<FormElementBase>,
) => {
  // Create a fresh copy of the component to avoid carrying over properties from previous components
  pendingComponent.value = JSON.parse(JSON.stringify(component));
  canDropInRepeater.value = component.type !== FormElementType.REPEATER;
};
</script>

<template>
  <div class="form-designer-page">
    <el-alert
      v-if="requiresDraft && !isDraftMode"
      class="draft-banner"
      type="info"
      show-icon
      :closable="false"
      title="This form is in use by workflows, so it's read-only. Click 'Edit as Draft' to make changes safely — nothing goes live until you commit."
    />
    <el-alert
      v-if="isDraftMode"
      class="draft-banner"
      type="warning"
      show-icon
      :closable="false"
      title="DRAFT — this form is in use, so you are editing a draft. Changes do not affect the live form (or its workflows) until the draft is committed."
    />
    <div class="form-designer-buttons">
      <el-button
        v-if="requiresDraft && !isDraftMode && hasSetupBasicRole"
        class="editDraftButton"
        type="primary"
        :loading="enteringDraft"
        @click="handleEnterDraft"
        >Edit as Draft
      </el-button>
      <el-button
        v-if="isDraftMode && hasSetupBasicRole && canEditForm"
        class="discardDraftButton"
        type="danger"
        plain
        :loading="discardingDraft"
        :disabled="committingDraft"
        @click="handleDiscardDraft"
        >Discard Draft
      </el-button>
      <el-tooltip
        v-if="isDraftMode && hasSetupBasicRole && canEditForm"
        :disabled="draftHasChanges"
        content="No changes to commit yet — edit the form first."
        placement="bottom"
      >
        <span class="commitDraftButton">
          <el-button
            type="success"
            :loading="committingDraft"
            :disabled="discardingDraft || !draftHasChanges"
            @click="handleCommitDraft"
            >Commit Draft
          </el-button>
        </span>
      </el-tooltip>
      <el-button
        v-if="hasSetupBasicRole && canEditForm"
        class="cancelButton"
        :disabled="!saveNeeded"
        @click="handleCancelChanges"
        >Cancel
      </el-button>
      <el-button
        v-if="hasSetupBasicRole && canEditForm"
        class="saveButton"
        :disabled="!saveNeeded"
        :class="{ flash: saveNeeded }"
        type="primary"
        @click="handleSaveChanges"
        >Save
      </el-button>
    </div>
    <el-tabs v-model="activeTab" type="card" width="100%">
      <el-tab-pane label="Form Properties" name="properties">
        <div class="properties">
          <FormPropertiesEditor
            class="formPropertiesEditor"
            :model-value="form"
            :compliance-item="complianceItem"
            :workflow-definition="props.workflowDefinition"
            :read-only="!hasSetupBasicRole || !canEditForm"
            @update:model-value="handleUpdateFormProperties"
            @close-drawer="emit('closeDrawer')"
            @deleted="emit('deleted', $event)"
          />
          <div class="buttons">
            <el-button
              v-if="form.formType !== FormType.WORKFLOW_DEFINITION_MAIN_FORM"
              class="deleteFormButton"
              plain
              :disabled="!hasSetupBasicRole || !canEditForm"
              type="danger"
              @click="handleDeleteFormAndResponses(form.guid)"
            >
              Delete Form & All Responses
            </el-button>
            <div>
              <el-button
                class="duplicateFormButton"
                plain
                :disabled="!hasSetupBasicRole || !canEditForm"
                :loading="isDuplicating"
                @click="handleDuplicateForm(form.guid)"
              >
                Duplicate Form
              </el-button>
              <div class="comment">Guid: {{ form.guid }}</div>
            </div>
          </div>
        </div>
      </el-tab-pane>
      <el-tab-pane label="Design" name="design">
        <div
          class="designer-section form-designer"
          :style="{
            height:
              !props.workflowDefinition && !props.complianceItem ? '100vh' : '',
          }"
        >
          <div class="formRendererWrapper">
            <div class="formRenderer">
              <FormRendererInner
                :key="lastSaveTime.toString()"
                v-model="form"
                v-model:page-number="currentPageNumber"
                :split-pages="splitFormPages"
                :disable-rules-engine="!applyRules"
                @update:form-pages="(val: FormPage[]) => (formPages = val)"
              >
                <template
                  #component="{ component }: { component: FormElementBase }"
                >
                  <div
                    v-if="!component.isArchived || showArchived"
                    :draggable="hasSetupBasicRole && canEditForm"
                    class="component-wrapper"
                    @click="() => handleClickComponent(component)"
                    @dragstart="
                      hasSetupBasicRole &&
                      canEditForm &&
                      handleOnDrag($event, component)
                    "
                    @dragend="
                      hasSetupBasicRole &&
                      canEditForm &&
                      handleOnDrag($event, component)
                    "
                  >
                    <DroppablePad
                      :id="component.guid || component.questionCode"
                      :key="component.guid"
                      :is-dragging="isDragging"
                      @drop="handleDropComponent"
                    >
                      <FormComponentRenderer
                        :model-value="component"
                        :show-question-codes="
                          showQuestionCodes || isRuleDrawerOpen
                        "
                        :display-mode="
                          !hasSetupBasicRole || !canEditForm
                            ? FormDisplayMode.READ_ONLY
                            : undefined
                        "
                        :show-archived="showArchived"
                        :class="{
                          highlight: editingComponent === component,
                          'section-selected':
                            editingComponent?.guid === component.parentGuid,
                          [component.questionCode]: true,
                        }"
                      />
                    </DroppablePad>
                  </div>
                </template>
              </FormRendererInner>
            </div>
          </div>
          <div class="sidePanelWrapper">
            <section class="sidePanel">
              <div class="formTopOptions">
                <div class="formButtons">
                  <el-button
                    :disabled="!hasSetupBasicRole || !canEditForm"
                    @click="handleOpenRuleDrawer"
                    >Edit Rules</el-button
                  >
                  <el-button
                    v-if="
                      concurrencyMode === FormConcurrencyMode.REQUEST_CONTROL &&
                      hasSetupBasicRole &&
                      !collaborationAllowsEdit &&
                      !isDraftLockActive
                    "
                    :loading="controlRequestPending"
                    type="warning"
                    @click="handleRequestControl"
                    >Request Control</el-button
                  >
                </div>

                <div class="table-of-contents">
                  <el-popover
                    trigger="hover"
                    placement="bottom"
                    width="auto"
                    :popper-style="{ maxHeight: '75vh', overflow: 'auto' }"
                  >
                    <template #reference>
                      <el-button
                        circle
                        :icon="Expand"
                        title="Table of Contents"
                      />
                    </template>
                    <TableOfContents
                      :form-pages="formPages"
                      :form="form"
                      @switch-page="(val: number) => (currentPageNumber = val)"
                      @handle-select-menu-item="handleClickComponent"
                    />
                  </el-popover>
                </div>

                <div class="formOptionButtons">
                  <el-checkbox
                    v-model="showQuestionCodes"
                    label="Show Question Codes"
                    title="Press q to toggle"
                  />
                  <el-checkbox
                    v-model="autoSaveForm"
                    label="Autosave Form"
                    :disabled="!hasSetupBasicRole || !canEditForm"
                  />
                  <el-checkbox
                    v-model="splitFormPages"
                    label="Split Pages"
                    :disabled="!hasSetupBasicRole || !canEditForm"
                  />
                  <el-checkbox
                    v-model="applyRules"
                    label="Apply Rules"
                    :disabled="!hasSetupBasicRole || !canEditForm"
                  />
                  <el-checkbox
                    v-if="numberArchived"
                    v-model="showArchived"
                    :label="`Show Archived (${numberArchived})`"
                  />
                </div>
              </div>

              <ElementEditor
                v-if="editingComponent"
                :key="_editingComponentGuid"
                v-model:form="form"
                v-model="editingComponent"
                class="elementPropertiesEditor"
                :can-delete="
                  !elementsThatCantBeDeleted.includes(editingComponent.guid) &&
                  !editingComponent.isArchived
                "
                :section-assignables="sectionAssignables"
                :save-needed="saveNeeded"
                :workflow-definition="props.workflowDefinition"
                :active-tab="activeElementEditorTab"
                :responses-exist="responsesExist"
                :entity-definitions-list="entityDefinitions"
                :form-definitions-list="formDefinitionsList"
                :list-definitions-list="listDefinitionsList"
                :event-codes-list="eventCodesList"
                :security-groups-list="securityGroups"
                :read-only="!hasSetupBasicRole || !canEditForm"
                @delete:form-element="handleDeleteElementFromWorkflow"
                @archive:form-element="handleArchiveElementFromWorkflow"
                @unarchive:form-element="handleUnarchiveElementFromWorkflow"
                @stop-editing:form-element="handleStopEditing"
                @update:active-tab="
                  (val: string) => (activeElementEditorTab = val)
                "
              />

              <CompliancePalette
                v-if="form.formType !== FormType.COMPLIANCE_ITEM_DEFINITION"
                v-model:is-dragging="isDragging"
                :form-components="form.components"
                :form="form"
                :read-only="!hasSetupBasicRole || !canEditForm"
                @component="handlePendingComponent"
              />
              <FormDesignerPalette
                v-model:is-dragging="isDragging"
                :read-only="!hasSetupBasicRole || !canEditForm"
                @component="handlePendingComponent"
              />
            </section>
          </div>
          <el-icon
            class="pageTipIcon"
            title="View Tips"
            @click="showTipsDialog = true"
          >
            <el-icon-question-filled />
          </el-icon>
        </div>
      </el-tab-pane>

      <el-tab-pane
        v-if="props.workflowDefinition"
        label="Versions"
        name="versions"
        lazy
      >
        <VersionsTab
          v-model:form="form"
          :workflow-definition="props.workflowDefinition"
          :read-only="!hasSetupBasicRole || !canEditForm"
        />
      </el-tab-pane>

      <el-tab-pane label="Access" name="restrictions">
        <FormRule
          v-model:form="form"
          :rule-type="FormRuleType.IS_RESTRICTED_TO"
          :read-only="!hasSetupBasicRole || !canEditForm"
          :assignables="sectionAssignables"
        />
      </el-tab-pane>
      <el-tab-pane label="Lists" name="lists" lazy>
        <ListsTab
          v-if="form.guid && props.workflowDefinition?.guid"
          :form-definition-guid="form.guid"
          :workflow-definition-guid="props.workflowDefinition?.guid"
          :readonly="!hasSetupBasicRole || !canEditForm"
        />
      </el-tab-pane>
      <el-tab-pane v-if="hasSetupBasicRole" label="History" name="history" lazy>
        <FormHistoryTab v-if="form.guid" :form-definition-guid="form.guid" />
      </el-tab-pane>
      <!-- <el-tab-pane label="Reviews" name="reviews">
        <FormRule v-model:form="form" :rule-type="FormRuleType.REQUIRES_REVIEW" :assignables="sectionAssignables" />
      </el-tab-pane> -->
      <!-- <el-tab-pane label="Approvals" name="approvals">
        <FormRule v-model:form="form" :rule-type="FormRuleType.REQUIRES_APPROVAL" :assignables="sectionAssignables" />
      </el-tab-pane> -->
    </el-tabs>

    <FormJsonUploader
      :key="uploaderOpenCounter"
      v-model="formUploaderVisible"
      :form-definition-guid="form.guid"
      @update:form="
        ($event: FormDefinition) => {
          form = $event;
          lastSaveTime = new Date();
        }
      "
    />
    <el-drawer
      v-model="isRuleDrawerOpen"
      title="Edit Rules"
      direction="rtl"
      modal-class="drawer-modal"
      :size="drawerWidth"
      @closed="handleCloseRuleDrawer"
    >
      <FormRulesTable
        v-model="form"
        :workflow-definition="props.workflowDefinition"
        :section-assignables="sectionAssignables"
        @close-rule-drawer="handleCloseRuleDrawer"
      />
    </el-drawer>

    <el-dialog
      ref="tipsDialog"
      v-model="showTipsDialog"
      class="tipsDialog"
      :append-to-body="true"
      title="Keyboard Tips"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <table>
        <thead>
          <tr>
            <th colspan="2">General</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="left"><span class="key">Esc</span> :</td>
            <td class="right">Close "Properties Panel"</td>
          </tr>
          <tr>
            <td class="left"><span class="key">q</span> :</td>
            <td class="right">Toggle "Show Question Code"</td>
          </tr>
          <tr>
            <td class="left"><span class="key">r</span> :</td>
            <td class="right">Open "Edit Rules" panel</td>
          </tr>
          <tr>
            <td class="left">
              <span class="key">/</span> or <span class="key">?</span> :
            </td>
            <td class="right">Show/hide these tips.</td>
          </tr>
        </tbody>
      </table>
    </el-dialog>
  </div>
</template>

<style lang="less">
.form-designer-page {
  .el-drawer__body {
    overflow-y: auto;
  }
  .el-drawer__header {
    margin-bottom: 0;
  }
  .el-drawer__title {
    font-size: 2rem;
    color: black;
  }
  .el-drawer__body,
  .formContainer,
  .form-designer {
    height: 100%;
  }
  // display: grid;

  .form-designer-buttons {
    display: flex;
    gap: 10px;

    .saveButton {
      position: fixed;
      right: 20px;
      z-index: 100;
    }
    .cancelButton {
      position: fixed;
      right: 104px;
      z-index: 100;
    }
    .commitDraftButton {
      position: fixed;
      right: 205px;
      z-index: 100;
    }
    .discardDraftButton {
      position: fixed;
      right: 359px;
      z-index: 100;
    }
    .editDraftButton {
      position: fixed;
      right: 20px;
      z-index: 100;
    }
  }

  .draft-banner {
    margin-bottom: 8px;
  }

  .form-designer {
    display: grid;
    grid-template-columns: minmax(350px, 1fr) minmax(250px, 1fr);
    grid-template-areas: "form sidePanel";
    column-gap: 20px;
    row-gap: 8px;
    max-width: 1800px;

    .sidePanelWrapper {
      overflow-y: auto;
    }

    .sidePanel {
      grid-area: sidePanel;
      position: sticky;
      top: 0;
      height: min-content;
      // background-color: red;
      gap: 16px;

      // for the panels on the side
      > div {
        height: min-content;
        box-shadow: inset 0px 0px 4px #666;
        border-radius: var(--el-border-radius-base);
        display: flex;
        flex-direction: column;

        & + div {
          margin-top: 1em;
        }
      }
      .elementPropertiesEditor {
        background-color: var(--el-color-success-light-9);
      }
    }

    .tipsDialog {
      table {
        width: 100%;
        th {
          font-size: 1.2em;
          padding: 0.5em 0;
        }
        td {
          padding: 0.5em 0;
          .key {
            font-weight: bold;
            font-size: 0.1em;
          }
        }
        .left {
          text-align: right;
        }
      }
    }

    .pageTipIcon {
      position: fixed;
      bottom: 10px;
      right: 10px;
      font-size: 24px;
      cursor: pointer;
    }

    .formRendererWrapper {
      overflow-y: auto;

      .formRenderer {
        grid-area: form;
        display: block;
        width: 100%;
      }
    }

    div.formTopOptions {
      display: grid;
      border: none;
      box-shadow: none;
      grid-template-areas: "buttons toc options";
      grid-template-columns: 1fr auto auto;
      align-items: baseline;
      gap: 10px;

      .el-button {
        margin: 0;
      }
      .table-of-contents {
        grid-area: toc;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        width: 100%;
      }
      .formButtons {
        grid-area: buttons;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
      }

      .formOptionButtons {
        grid-area: options;
        display: flex;
        flex-wrap: wrap;
        column-gap: 10px;
        padding-left: 15px;
        .el-checkbox {
          color: var(--el-text-color-secondary);
          margin-right: 0;
          .el-checkbox__label {
            white-space: normal;
          }
        }
      }

      @keyframes flash {
        0% {
          background-color: #409eff; /* default primary color */
        }
        50% {
          background-color: #66b1ff; /* lighter primary color */
        }
        100% {
          background-color: #409eff;
        }
      }

      .flash {
        animation: flash 1s infinite;
      }
    }

    .designer-section {
      width: 100%;
    }

    .designable-settings-drawer {
      position: fixed;
      z-index: 100;
      bottom: 0px;
      left: 50%;
      transform: translateX(-50%);
      width: 1000px;
      // background: #fff;
      margin: 0px auto;
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      border: 2px solid var(--ep-color-primary);
    }

    h2 {
      font-size: 20px;
      margin: 0 0 10px 10px;

      div {
        font-weight: normal;
        font-size: small;
        line-height: 0.9;
        color: grey;
      }
    }
    .component-wrapper {
      position: relative;
      padding: 3px 0;
      display: flex;
      flex-direction: column;
      cursor: pointer;

      &:hover {
        background-color: #ffff8766;
      }

      .highlight {
        background-color: #e1f7d6;
      }

      .section-selected {
        background-color: #f0f9eb;
      }
    }
  }
  .properties {
    // Element Plus sets overflow:hidden on .el-tabs__content, which clips a tall Form Properties
    // tab. Make the pane itself scroll so all fields (and the delete button) stay reachable.
    max-height: 75vh;
    overflow-y: auto;

    .formPropertiesEditor {
      padding: 1em 0em;
    }

    .buttons {
      margin-top: 3em;
      margin-left: 50px;
    }
  }

  .buttons {
    margin-top: 3em;
    margin-left: 50px;
    display: flex;
    justify-content: space-between;

    .comment {
      margin-top: 1em;
      color: #555;
      font-size: 0.8em;
    }
  }
  .inPdf,
  .notInPdf {
    position: relative; /* Ensure the pseudo-element is positioned relative to this element */
  }
  .inPdf::before,
  .notInPdf::before {
    position: absolute;
    right: 0px;
    top: 4px;
    display: block;
    width: 16px; /* Adjust to your icon's width */
    height: 16px; /* Adjust to your icon's height */
    background-image: url("/images/pdf.png"); /* Path to your icon */
    background-size: contain; /* Ensure the icon fits within the dimensions */
    background-repeat: no-repeat;
  }
  .inPdf::before {
    content: "";
  }
  .notInPdf::before {
    content: "X";
    color: orangered;
    font-size: 28px;
    line-height: 16px;
    text-align: center;
    font-weight: 200;
  }

  .el-overlay.drawer-modal {
    background-color: rgba(0, 0, 0, 0.2);
  }
}
</style>
